/**
 * ApplyVerificationModal Component
 * Modal for applying/upgrading verification tier
 */
import React, { useState, useEffect, useRef } from 'react';
import TierBadge from './TierBadge';

const TIER_INFO = {
  T1: {
    name: 'Verified',
    description: 'Identity verified & payout-ready',
    icon: '✓'
  },
  T2: {
    name: 'Pro',
    description: 'High ratings, proven track record',
    icon: '⭐'
  },
  T3: {
    name: 'Elite Partner',
    description: 'Sustained excellence',
    icon: '🛡️'
  }
};

const CHECKLIST_GROUPS = {
  identity: {
    title: 'Identity & Payout',
    keys: ['id_verified', 'stripe_payout_ready', 'kyb_verified']
  },
  profile: {
    title: 'Profile',
    keys: ['profile_complete', 'portfolio_items>=3']
  },
  performance: {
    title: 'Performance (90 days)',
    keys: ['orders_completed>=10', 'nails_avg>=4.7', 'on_time_rate>=0.90', 'dispute_rate<0.05', 'median_first_reply_hours<=6']
  },
  quality: {
    title: 'Quality Review',
    keys: ['portfolio_checked']
  },
  elite: {
    title: 'Elite Requirements',
    keys: ['lifetime_orders>=40', 'nails_avg>=5.1', 'on_time_rate>=0.95', 'dispute_rate<0.02', 'interview_passed']
  }
};

const ApplyVerificationModal = ({ open, onClose, current, onApply, onRecheck }) => {
  const [selectedTier, setSelectedTier] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const modalRef = useRef(null);
  const firstFocusableRef = useRef(null);
  
  // Focus trap
  useEffect(() => {
    if (open && firstFocusableRef.current) {
      firstFocusableRef.current.focus();
    }
  }, [open]);
  
  // ESC key handler
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && open) {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [open, onClose]);
  
  if (!open || !current) return null;
  
  const currentTierNum = parseInt(current.tier[1]) || 0;
  
  const handleApply = async () => {
    if (!selectedTier) return;
    
    setIsSubmitting(true);
    try {
      await onApply(selectedTier);
      onClose();
    } catch (err) {
      // Error handled by parent
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleRecheck = async () => {
    setIsSubmitting(true);
    try {
      await onRecheck();
    } catch (err) {
      // Error handled by parent
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const groupChecklistItems = () => {
    const grouped = {};
    
    if (!current.checklist) return grouped;
    
    Object.keys(CHECKLIST_GROUPS).forEach(groupKey => {
      const group = CHECKLIST_GROUPS[groupKey];
      const items = current.checklist.filter(item => group.keys.includes(item.key));
      if (items.length > 0) {
        grouped[groupKey] = { ...group, items };
      }
    });
    
    return grouped;
  };
  
  const groupedChecklist = groupChecklistItems();
  
  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="verification-modal-title"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        ref={modalRef}
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <TierBadge tier={current.tier} size="md" showUnverified />
            <h2 id="verification-modal-title" className="text-2xl font-bold">
              {current.tier === 'T0' ? 'Get Verified' : 'Upgrade Verification'}
            </h2>
          </div>
          <button
            ref={firstFocusableRef}
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded p-1"
            aria-label="Close modal"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Status */}
        {current.status !== 'none' && (
          <div className="p-4 bg-gray-50 border-b">
            <div className="flex items-center gap-2">
              <span className="font-medium">Status:</span>
              <span className={`
                px-3 py-1 rounded-full text-sm font-medium
                ${current.status === 'approved' ? 'bg-green-100 text-green-700' : ''}
                ${current.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : ''}
                ${current.status === 'denied' ? 'bg-red-100 text-red-700' : ''}
              `}>
                {current.status.charAt(0).toUpperCase() + current.status.slice(1)}
              </span>
              {current.requested_tier && (
                <span className="text-gray-600">
                  · Requested: <TierBadge tier={current.requested_tier} size="xs" />
                </span>
              )}
            </div>
          </div>
        )}
        
        {/* Tier Selection */}
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold mb-4">Select Tier</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {['T1', 'T2', 'T3'].map(tier => {
              const tierNum = parseInt(tier[1]);
              const isDisabled = tierNum <= currentTierNum && current.status === 'approved';
              const isSelected = selectedTier === tier;
              
              return (
                <button
                  key={tier}
                  onClick={() => !isDisabled && setSelectedTier(tier)}
                  disabled={isDisabled}
                  className={`
                    p-4 rounded-lg border-2 text-left transition-all
                    ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}
                    ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                    focus:outline-none focus:ring-2 focus:ring-blue-500
                  `}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">{TIER_INFO[tier].icon}</span>
                    <span className="font-bold">{TIER_INFO[tier].name}</span>
                  </div>
                  <p className="text-sm text-gray-600">{TIER_INFO[tier].description}</p>
                  {isDisabled && (
                    <p className="text-xs text-green-600 mt-2">✓ Already achieved</p>
                  )}
                </button>
              );
            })}
          </div>
        </div>
        
        {/* Checklist */}
        <div className="p-6 border-b">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Requirements</h3>
            <button
              onClick={handleRecheck}
              disabled={isSubmitting}
              className="text-sm text-blue-600 hover:text-blue-700 underline focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2 py-1"
            >
              {isSubmitting ? 'Rechecking...' : 'Recheck Now'}
            </button>
          </div>
          
          <div className="space-y-4" aria-describedby="checklist-description">
            <p id="checklist-description" className="sr-only">
              List of verification requirements showing which are met and which need completion
            </p>
            
            {Object.entries(groupedChecklist).map(([groupKey, group]) => (
              <div key={groupKey} className="space-y-2">
                <h4 className="font-medium text-gray-700">{group.title}</h4>
                <div className="space-y-1">
                  {group.items.map(item => (
                    <div
                      key={item.key}
                      className="flex items-center gap-2 text-sm"
                    >
                      <span
                        className={`
                          flex-shrink-0 w-5 h-5 rounded flex items-center justify-center
                          ${item.ok ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}
                        `}
                        aria-label={item.ok ? 'Completed' : 'Not completed'}
                      >
                        {item.ok ? '✓' : '○'}
                      </span>
                      <span className={item.ok ? 'text-gray-700' : 'text-gray-500'}>
                        {item.label}
                      </span>
                      <span className="text-xs text-gray-400">
                        ({item.required_tier})
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Footer */}
        <div className="p-6 bg-gray-50 flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-500 rounded"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            disabled={!selectedTier || isSubmitting}
            className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {isSubmitting ? 'Submitting...' : 'Request Verification'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ApplyVerificationModal;
